import { motion } from "framer-motion";

/**
 * Editorial image frame with gold corner marks and reveal.
 * Variants: "wide" (cinematic letterbox), "portrait" (tall right column), "square".
 * duotone  -> unified brand green duotone treatment.
 * fullBleed -> breaks out of the parent container to span the full viewport width.
 */
export default function FramedImage({
  src,
  alt,
  variant = "wide",
  tint = "dark",
  bare = false,
  shadow = false,
  duotone = false,
  fullBleed = false,
  className = "",
}) {
  const aspect =
    variant === "wide"
      ? "aspect-[21/9]"
      : variant === "portrait"
      ? "aspect-[3/4]"
      : "aspect-square";

  const bleed = fullBleed
    ? "w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw]"
    : "";

  return (
    <motion.figure
      initial={{ opacity: 0, y: 24, scale: 1.03 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
      className={`relative ${bleed} ${className}`}
    >
      <div
        className={`relative ${aspect} ${shadow ? "" : "overflow-hidden"} ${
          !bare && !fullBleed ? "rounded-xl" : ""
        } ${bare && tint === "light" ? "bg-[#F7F9F8]" : bare && shadow ? "bg-transparent" : "bg-[#06231D]"}`}
      >
        <img
          src={src}
          alt={alt}
          loading="lazy"
          draggable={false}
          className={`absolute inset-0 w-full h-full select-none ${bare ? "object-contain scale-[1.07]" : "object-cover"} ${
            duotone ? "grayscale contrast-[1.08] brightness-[1.03]" : ""
          }`}
          style={shadow ? { filter: "drop-shadow(0 0 24px rgba(46,196,138,0.38)) drop-shadow(0 0 48px rgba(46,196,138,0.18)) drop-shadow(0 28px 50px rgba(0,0,0,0.55)) drop-shadow(0 8px 16px rgba(0,0,0,0.35))" } : undefined}
        />

        {/* Duotone recolor: grayscale image + brand gradient blended by hue/sat */}
        {duotone && (
          <>
            <div
              aria-hidden
              className="absolute inset-0 mix-blend-color"
              style={{ background: "linear-gradient(135deg, #06231D 0%, #0d5a48 55%, #2EC48A 100%)" }}
            />
            <div
              aria-hidden
              className="absolute inset-0 mix-blend-multiply opacity-40"
              style={{ background: "linear-gradient(180deg, rgba(6,35,29,0.2) 0%, rgba(6,35,29,0.85) 100%)" }}
            />
          </>
        )}

        {/* brand-toned overlay for cohesion */}
        {!bare && !duotone && (
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                tint === "dark"
                  ? "linear-gradient(180deg, rgba(6,35,29,0.35) 0%, rgba(6,35,29,0.7) 100%)"
                  : "linear-gradient(180deg, rgba(6,35,29,0.2) 0%, rgba(6,35,29,0.45) 100%)",
            }}
          />
        )}

        {/* corner marks */}
        {!bare && (
          <div className="absolute inset-4 pointer-events-none">
            <div className="absolute top-0 left-0 w-3 h-px bg-[#C9A84C]" />
            <div className="absolute top-0 left-0 w-px h-3 bg-[#C9A84C]" />
            <div className="absolute top-0 right-0 w-3 h-px bg-[#C9A84C]" />
            <div className="absolute top-0 right-0 w-px h-3 bg-[#C9A84C]" />
            <div className="absolute bottom-0 left-0 w-3 h-px bg-[#C9A84C]" />
            <div className="absolute bottom-0 left-0 w-px h-3 bg-[#C9A84C]" />
            <div className="absolute bottom-0 right-0 w-3 h-px bg-[#C9A84C]" />
            <div className="absolute bottom-0 right-0 w-px h-3 bg-[#C9A84C]" />
          </div>
        )}
      </div>
    </motion.figure>
  );
}
